/**
 * Offers (module 5, ADR 0010): the data of one offer variant and the
 * calculation of the office's template "Angebotskalkulation" (version 2):
 * own work = hours × rate per role × (1 + surcharge on own work); work of a
 * subcontractor = fixed price or hours × purchase rate, plus that
 * subcontractor's surcharge; a position's price = its costs rounded UP to
 * the rounding step. Pure, used by the server and the browser alike; the
 * spreadsheet PST fills is recalculated by LibreOffice and checked against
 * this (AP-93).
 */
export const FEE_BASES = ['hours', 'hoai', 'both'];
export const CONSTELLATIONS = ['einzel', 'einzel_nu', 'bg', 'bg_nu'];
/** As the template's drop-down in 01_Parameter C3 spells them. */
export const CONSTELLATION_TEMPLATE = {
    einzel: 'Einzelbieter',
    einzel_nu: 'Einzelbieter mit Nachunternehmer',
    bg: 'Bietergemeinschaft',
    bg_nu: 'Bietergemeinschaft mit Nachunternehmer',
};
/** Whether the constellation has members of a joint bid, and subcontractors. */
export const isJointBid = (c) => c === 'bg' || c === 'bg_nu';
export const hasSubcontractors = (c) => c === 'einzel_nu' || c === 'bg_nu';
/** At most as many as the template has room for. */
export const OFFER_LIMITS = { roles: 6, parties: 6, subcontractors: 3, positions: 20, extras: 16, cuts: 3 };
export const DEFAULT_PARAMS = {
    showHours: false,
    hintPrices: 'Die Positionen sind einzeln beauftragbar. Bei einer Teilbeauftragung entsteht kein Aufschlag; entfallende Positionen entfallen ersatzlos.',
    hintExtras: 'Zusatzleistungen werden ausschließlich nach vorheriger schriftlicher Freigabe erbracht.',
    subcontractorAnnex: true,
    surchargeOwn: 0,
    roundPositions: 100,
    roundExtras: 50,
    vat: 0.19,
    minRate: null,
    subcontractorHint: 'Die Anteile des Nachunternehmers sind zu den genannten Konditionen in den Positionspreisen enthalten.',
    discount: 0.1,
};
export function emptyVariantData() {
    return {
        roles: [{ short: 'R1', name: '', rate: null, basis: '' }],
        partyIds: [],
        subcontractors: [],
        params: { ...DEFAULT_PARAMS },
        positions: [],
        extras: [],
        cutNames: ['', '', ''],
    };
}
const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
/** CEILING(x, step) as in the spreadsheet; the step is at least 0.01. */
export function ceilTo(x, step) {
    const s = Math.max(step, 0.01);
    // Guard against 1200.0000000001 becoming 1300.
    return Math.ceil(Math.round((x / s) * 1e9) / 1e9) * s;
}
function line(hours, shares, data, step, extra) {
    const rates = data.roles.map((r) => n(r.rate));
    const ownHours = hours.reduce((s, h) => s + n(h), 0);
    const own = hours.reduce((s, h, i) => s + n(h) * (rates[i] ?? 0), 0) * (1 + n(data.params.surchargeOwn));
    let purchase = 0;
    let surcharges = 0;
    let subcontractorHours = 0;
    data.subcontractors.forEach((sub, i) => {
        const share = shares[i];
        if (!share)
            return;
        const p = n(share.fixed) > 0 ? n(share.fixed) : n(share.hours) * n(sub.purchaseRate);
        purchase += p;
        surcharges += p * n(sub.surcharge);
        if (n(share.fixed) <= 0)
            subcontractorHours += n(share.hours);
    });
    const foreign = purchase + surcharges;
    const cost = own + foreign;
    const price = extra && cost === 0 ? null : ceilTo(cost, step);
    const parts = [];
    data.roles.forEach((r, i) => { if (n(hours[i]) > 0)
        parts.push(`${fmt(n(hours[i]))} ${r.short}`); });
    data.subcontractors.forEach((sub, i) => {
        const s = shares[i];
        if (n(s?.hours) > 0)
            parts.push(`${fmt(n(s.hours))} ${sub.short}`);
        if (n(s?.fixed) > 0)
            parts.push(`Pauschale ${sub.short}`);
    });
    const ownRoles = hours.map((h, i) => (n(h) > 0 ? i : -1)).filter((i) => i >= 0);
    const count = ownRoles.length + data.subcontractors.reduce((c, _, i) => c + (n(shares[i]?.hours) > 0 ? 1 : 0) + (n(shares[i]?.fixed) > 0 ? 1 : 0), 0);
    let rateNote = '';
    if (count > 1)
        rateNote = 'je Rolle';
    else if (ownRoles.length === 1)
        rateNote = rates[ownRoles[0]] ?? 0;
    else if (count === 1) {
        const i = data.subcontractors.findIndex((_, k) => n(shares[k]?.hours) > 0);
        const sub = i >= 0 ? data.subcontractors[i] : undefined;
        rateNote = sub && sub.billing === 'Stunden' && sub.purchaseRate !== null ? n(sub.purchaseRate) * (1 + n(sub.surcharge)) : 'Pauschale';
    }
    return { ownHours, own, purchase, foreign, cost, price, rounding: price === null ? 0 : price - cost, subcontractorHours, hoursNote: parts.join(' + '), rateNote };
}
/** Numbers as the spreadsheet writes them into text: 12 or 12.5. */
const fmt = (x) => String(Math.round(x * 100) / 100);
export function calculateVariant(data) {
    const positions = data.positions.map((p) => line(p.hours, p.shares, data, data.params.roundPositions, false));
    const extras = data.extras.map((e) => line(e.hours, e.shares, data, data.params.roundExtras, true));
    const sum = (f) => positions.reduce((s, l) => s + f(l), 0);
    const net = sum((l) => n(l.price));
    const own = sum((l) => l.own);
    const foreign = sum((l) => l.foreign);
    const ownHours = sum((l) => l.ownHours);
    const rates = data.roles.map((r) => r.rate).filter((r) => typeof r === 'number');
    const threshold = data.params.minRate ?? (rates.length ? Math.min(...rates) : 0);
    const revenuePerHour = ownHours === 0 ? null : (net - foreign) / ownHours;
    const perSubcontractor = data.subcontractors.map((sub, i) => {
        const purchase = data.positions.reduce((s, p) => {
            const sh = p.shares[i];
            return s + (n(sh?.fixed) > 0 ? n(sh?.fixed) : n(sh?.hours) * n(sub.purchaseRate));
        }, 0);
        return { purchase, withSurcharge: purchase * (1 + n(sub.surcharge)) };
    });
    const perRole = data.roles.map((r, i) => {
        const hours = data.positions.reduce((s, p) => s + n(p.hours[i]), 0);
        return { hours, amount: hours * n(r.rate) };
    });
    const rounding = sum((l) => l.rounding);
    const ownWithRounding = own + rounding;
    const afterDiscount = ceilTo(foreign + ownWithRounding * (1 - n(data.params.discount)), Math.max(data.params.roundPositions, 0.01));
    const discountHours = revenuePerHour ? (ownWithRounding * n(data.params.discount)) / revenuePerHour : null;
    const cuts = [0, 1, 2].map((c) => {
        const kept = data.positions.map((p, i) => (p.cuts[c] ? positions[i] : null)).filter((l) => l !== null);
        const cn = kept.reduce((s, l) => s + n(l.price), 0);
        const ch = kept.reduce((s, l) => s + l.ownHours, 0);
        const cf = kept.reduce((s, l) => s + l.foreign, 0);
        return { net: cn, ownHours: ch, foreign: cf, revenuePerHour: ch === 0 ? null : (cn - cf) / ch };
    });
    const vatAmount = net * n(data.params.vat);
    return {
        positions, extras, net, vatAmount, gross: net + vatAmount, own, foreign, rounding, ownHours,
        subcontractorHours: sum((l) => l.subcontractorHours),
        revenuePerHour, threshold, belowThreshold: revenuePerHour !== null && revenuePerHour < threshold,
        foreignShare: net === 0 ? 0 : perSubcontractor.reduce((s, x) => s + x.withSurcharge, 0) / net,
        perSubcontractor, perRole, afterDiscount, discountHours, cuts,
        afterDiscountPerHour: ownHours === 0 ? null : (afterDiscount - foreign) / ownHours,
    };
}
export const EMPTY_PROFILE = { career: [], volunteer: [], education: [], qualifications: [], languages: '', software: '' };
export const EMPTY_ATTACHMENTS = { cvPartyIds: [], cvBirthDate: false, cvProjectQualifications: [], referenceIds: [], commitments: false, agreements: false, hoaiAnnex: false };
export const OFFER_STATUSES = ['entwurf', 'abgegeben', 'beauftragt', 'abgelehnt', 'zurückgezogen'];
export const EMPTY_TEXTS = {
    clientContact: '', clientStreet: '', clientCity: '', salutation: '', theirRef: '', tenderDate: '', reference: '', subject: '',
    intro: '', legalForm: '', qualifications: [], annexes: [], region: '', delimitations: [], assumptions: [],
    daysDocuments: '', daysApprovals: '', cooperation: [], start: '', duration: '', payment: '', place: '', scope: '', jointBid: '',
    signPlace: '', coverPersons: '', coverAssets: '', questions: [], risks: [], priceAnnex: false, attachments: EMPTY_ATTACHMENTS,
};
//# sourceMappingURL=offers.js.map